<template>
	<div
		class="amelipro-select__wrapper"
		:class="selectClasses"
		:style="globalFieldStyles"
	>
		<div
			class="d-inline-flex align-start"
			:class="labelClasses"
		>
			<label
				:id="`${id}-label`"
				class="mr-2 amelipro-select__label"
				:for="id"
				:style="labelStyles"
			>
				{{ label }}

				<span v-if="ariaRequired">
					<span aria-hidden="true">
						&nbsp;*
					</span>

					<span class="d-sr-only">
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<slot name="labelInfo" />
		</div>

		<VSelect
			:id="id"
			ref="inputSelect"
			v-model="inputValue"
			:aria-describedby="describedby"
			:aria-required="ariaRequired"
			:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
			class="pt-0 amelipro-select"
			:clearable="clearable"
			color="ap-blue darken-1"
			dense
			:disabled="disabled"
			:hide-details="hideErrorMessage"
			:items="items"
			outlined
			:placeholder="placeholder"
			:readonly="readonly"
			:rules="selectRules"
			:style="inputStyles"
			v-bind="$attrs"
			v-on="$listeners"
			@blur="focused = false"
			@focus="focused = true"
		>
			<template #append-outer>
				<slot name="append-outer" />
			</template>

			<template #message="{ message }">
				<AmeliproMessage
					:id="errorId"
					no-icon
					text
					type="error"
				>
					<p class="mb-0">
						{{ message }}
					</p>
				</AmeliproMessage>
			</template>
		</VSelect>
	</div>
</template>

<script setup lang="ts">
	import { InputSelect, SelectItem } from './types';
	import { PropType, computed, ref, watch } from 'vue';
	import AmeliproMessage from '../AmeliproMessage';
	import { IndexedObject } from '../../types';
	import { VSelect } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		clearable: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		globalMaxWidth: {
			type: String,
			default: undefined,
		},
		globalMinWidth: {
			type: String,
			default: undefined,
		},
		globalWidth: {
			type: String,
			default: undefined,
		},
		hideErrorMessage: {
			type: [String, Boolean] as PropType<string | boolean>,
			default: false,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		inputMaxWidth: {
			type: String,
			default: undefined,
		},
		inputMinWidth: {
			type: String,
			default: undefined,
		},
		items: {
			type: Array as PropType<string[] | SelectItem[]>,
			required: true,
		},
		label: {
			type: String,
			required: true,
		},
		labelMaxWidth: {
			type: String,
			default: undefined,
		},
		labelMinWidth: {
			type: String,
			default: undefined,
		},
		placeholder: {
			type: String,
			default: undefined,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		value: {
			type: [Object, Number, String] as PropType<SelectItem | number | string>,
			default: undefined,
		},
	});

	// VSelect ref
	const inputSelect = ref<InputSelect>();

	// v-model
	const inputValue = ref<SelectItem | number | string>();
	watch(() => props.value, (value: SelectItem | number | string) => {
		inputValue.value = value;
	}, { immediate: true });

	const valid = ref(true);
	watch(() => inputValue.value, () => {
		valid.value = Boolean(inputSelect.value?.valid);
	});

	const focused = ref(false);
	const errorId = computed<string>(() => `${props.id}-error`);
	const describedby = computed<string | undefined>(() => (valid.value ? undefined : errorId.value));
	const displayError = computed(() => !inputSelect.value?.valid && inputSelect.value?.hasMessages);
	const selectRules = computed<ValidationRule[]>(() => {
		//const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	});

	// Fallback : nécessaire pour le bon fonctionnement d'autres composants
	// Ces autres composants devraient plutôt utiliser la valeur
	// retournée par v-model ou par @change
	defineExpose({ inputValue });

	const selectClasses = computed<string>(() => {
		const classes = ['d-flex', 'flex-column'];

		if (props.horizontal) {
			classes.push('flex-md-row');
		}

		return classes.join(' ');
	});

	const labelClasses = computed<string>(() => {
		let classes = '';

		if (props.horizontal) {
			classes = 'mt-md-2 mr-md-2';
		}

		return classes;
	});

	const globalFieldStyles = computed<IndexedObject>(() => ({
		maxWidth: props.globalMaxWidth,
		minWidth: props.globalMinWidth,
		width: props.globalWidth,
	}));

	const inputStyles = computed<IndexedObject>(() => ({
		maxWidth: props.inputMaxWidth,
		minWidth: props.inputMinWidth,
	}));

	const labelStyles = computed<IndexedObject>(() => ({
		maxWidth: props.labelMaxWidth,
		minWidth: props.labelMinWidth,
	}));

	const inputBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (focused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});
</script>
<style lang="scss" scoped>
.v-select {

	& :deep(.v-select__selection--comma) {
		line-height: 1.5rem;
	}

	:deep(.v-input__slot) {
		min-height: $input-min-height !important;
		border: v-bind(inputBorderStyle) !important;
		border-radius: $input-radius;

		fieldset {
			border: 0;
		}
	}
}

.amelipro-select__label {
	margin-bottom: 4px;
	font-weight: $label-font-weight;
}

:deep(.v-input__slot) {
	min-height: $input-min-height !important;
	border: v-bind(inputBorderStyle) !important;
	border-radius: $input-radius;

	fieldset {
		border: 0;
	}
}

.amelipro-select__label {
	margin-bottom: 4px;
	font-weight: $label-font-weight;
}

.v-list {
	& :deep(.v-list-item) .v-list-item__title {
		line-height: 1.5rem;
	}
}
</style>
